import { Component, Input, OnInit } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { GENDERS, IDTYPES, NON_CONFORMING_TITLES, REQUIRED_FIELDS_MISSING_MESSAGE, TITLES } from '@constants';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '@services/alert.service';
import { CameraService } from '@services/camera.service';
import { ConfigLocaleService } from '@services/configLocale.service';
import { UserService } from '@services/user.service';
import { DateTime } from 'luxon';
import { tap } from 'rxjs/operators';

@Component({
  selector: 'gc-member-information',
  templateUrl: './member-information.component.html',
  styleUrls: ['./member-information.component.scss'],
  standalone: false,
})
export class MemberInformationComponent implements OnInit {
  @Input() personalForm: FormGroup;
  @Input() editingUserAccount = false;
  idtypes = IDTYPES;
  maxDate = DateTime.now().toJSDate();
  profilePicture: string;
  identificationPicture: string;
  fullName: string;
  idRegex = '^.*$';

  idHidden = false;
  idRequired = false;

  constructor(
    private readonly userService: UserService,
    private readonly alertService: AlertService,
    private readonly camera: CameraService,
    private readonly configService: ConfigLocaleService,
    private readonly translateService: TranslateService,
  ) {}

  get titleOptions() {
    return TITLES;
  }

  get genderOptions() {
    return GENDERS;
  }

  ngOnInit() {
    this.personalForm.get('profile_picture').valueChanges.subscribe((value) => {
      this.profilePicture = value;
    });

    // if dob is of type luxon DateTime, convert to string
    this.personalForm.get('dob').valueChanges.subscribe((value) => {
      if (DateTime.isDateTime(value)) {
        this.personalForm.get('dob').setValue(value.toISODate());
      }
    });
    // Read configuration values synchronously via signals.
    this.idHidden = this.configService.$idHidden();
    if (!this.idHidden) {
      const idNumberValidators = this.configService.$idNumberValidatorsJson();
      try {
        this.idRegex = idNumberValidators.pattern || '^.*$';
        this.personalForm.get('idnumber').addValidators(Validators.pattern(new RegExp(idNumberValidators.pattern)));
        if (idNumberValidators.required) {
          this.idRequired = true;
          this.personalForm.get('idnumber').addValidators(Validators.required);
          this.personalForm.get('idnumber').updateValueAndValidity();
        }
      } catch (error) {
        console.error('Error parsing id_number_validators_json', error);
      }
    }
  }

  initials() {
    const initials = this.userService.extractInitials(this.personalForm.value.name);
    this.personalForm.get('initials').setValue(initials);
  }

  updatePicture(picture: string) {
    this.camera
      .openCamera(picture)
      .pipe(
        tap((i) => {
          if (i || i === null) {
            this.profilePicture = i;
            this.personalForm.patchValue({ profile_picture: i });
            if (this.editingUserAccount) {
              this.userService.updateUserProfile({ profile_picture: i }).subscribe(
                () => {
                  this.alertService.snackbar(
                    this.translateService.instant('profile_picture_successfully_updated'),
                    this.translateService.instant('success'),
                  );
                },
                (err: unknown) => {
                  this.alertService.handleErrorDialog$(err, this.translateService.instant('error_updating_profile_picture'));
                },
              );
            }
          }
        }),
      )
      .subscribe();
  }

  idtypeChange() {
    if (this.personalForm.get('idtype').value?.toString() === '0') {
      this.personalForm.get('idnumber').setValidators([Validators.pattern(this.idRegex), Validators.required]);
      this.personalForm.get('idnumber').updateValueAndValidity();
      this.personalForm.get('idnumber').setValue('');
    } else {
      this.personalForm.get('idnumber').clearValidators();
      this.personalForm.get('idnumber').updateValueAndValidity();
      this.personalForm.get('idnumber').setValue('');
    }
  }

  populateDobAndGender() {
    if (!this.personalForm.value.idnumber) {
      return;
    }
    const { dob, gender } = this.userService.extractDobAndGender(this.personalForm.value.idnumber);
    if (this.personalForm.value.dob !== dob) {
      this.personalForm.get('dob').setValue(dob);
    }
    if (this.personalForm.value.gender !== gender && !NON_CONFORMING_TITLES.includes(this.personalForm.value.title)) {
      this.personalForm.get('gender').setValue(gender);
    }
  }

  onSubmit() {
    if (this.personalForm.valid) {
      const personalInformation = this.personalForm.value;
      this.userService.updateUserProfile(personalInformation).subscribe(
        () => {
          this.alertService.snackbar('personal_information_updated');
          this.personalForm.markAsPristine();
        },
        (err: unknown) => {
          this.alertService.handleErrorDialog$(err, this.translateService.instant('error_updating_member_information'));
        },
      );
    } else {
      this.alertService.snackbar(REQUIRED_FIELDS_MISSING_MESSAGE);
      this.personalForm.markAllAsTouched();
    }
  }
}
